import { useMemo, useState, type FormEvent } from 'react';
import { ExternalLink, Plus, Search, Trash2 } from 'lucide-react';
import {
  calcularReferencia,
  dispersaoBps,
  formatarPct,
  formatarReais,
  type ModoReferencia,
} from '@profitlens/calculo';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { Campo, CampoInteiro, CampoQuantidade, CampoReais, Texto as CampoTexto } from '../componentes/Campos';
import { Quantidade, Reais, Texto } from '../componentes/Valor';
import { useAuth } from '../contextos/AuthContext';
import { useProdutos } from '../servicos/produtos';
import { useCriterios } from '../servicos/configuracoes';
import { anuncioVazio, removerAnuncio, salvarAnuncio, useAnuncios, type RascunhoAnuncio } from '../servicos/mercado';

/**
 * Quanto o produto está sendo vendido por aí.
 *
 * A coleta automática está bloqueada: toda a API do Mercado Livre passou a
 * exigir token, e a busca de anúncios responde 403 (pendência P1). Então isto
 * aqui é digitado — a pessoa abre o marketplace, olha e registra.
 *
 * O planejamento já previa esse cenário, e é por isso que o resto do sistema não
 * muda: o preço de referência sai do mesmo cálculo, venha o anúncio de onde
 * vier.
 */
const MODOS: { id: ModoReferencia; nome: string; porque: string }[] = [
  {
    id: 'MEDIANA',
    nome: 'Mediana',
    porque: 'O valor do meio. Um anúncio absurdo não move a conta — é o padrão mais seguro.',
  },
  {
    id: 'MEDIA_APARADA',
    nome: 'Média aparada',
    porque: 'Descarta os 10% extremos de cada ponta e tira a média do resto.',
  },
  {
    id: 'MENOR_VALIDA',
    nome: 'Menor válida',
    porque: 'O mais barato. Assume que você vai precisar competir com ele.',
  },
  { id: 'MEDIA', nome: 'Média simples', porque: 'Sensível a extremos. Use com poucos anúncios parecidos.' },
];

export function Pesquisa() {
  const { produtos, carregando } = useProdutos();
  const { criterios } = useCriterios();
  const { perfil, podeEscrever } = useAuth();
  const [produtoId, setProdutoId] = useState('');
  const [modo, setModo] = useState<ModoReferencia>('MEDIANA');
  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);

  const { anuncios, carregando: carregandoAnuncios } = useAnuncios(produtoId || null);
  const produto = produtos.find((p) => p.id === produtoId);

  const precos = useMemo(
    () =>
      anuncios
        .map((a) => a.precoUnitarioNormCents)
        .filter((p): p is number => typeof p === 'number' && p > 0),
    [anuncios],
  );

  const referencia = precos.length > 0 ? calcularReferencia(modo, precos) : null;
  const dispersao = dispersaoBps(precos);
  const minimo = criterios?.minAnunciosComp ?? 3;
  const dispersaoMaxima = criterios?.dispersaoMaximaBps ?? 4_000;

  if (carregando) return <Carregando />;

  return (
    <div className="flex flex-col gap-5">
      <Cabecalho
        titulo="Pesquisa de mercado"
        descricao="Por quanto o produto está sendo vendido. É o outro lado da conta: sem ele, só se sabe quanto custa comprar."
      />

      <div className="rounded-md border-l-3 border-c-res bg-c-res/10 px-4 py-3 text-sm text-tinta-2">
        <strong className="text-tinta">A coleta automática está bloqueada.</strong> Toda a API do
        Mercado Livre passou a exigir token, e a busca de anúncios responde 403 — é a pendência P1
        do planejamento. Então por enquanto isto é digitado: abra o marketplace, olhe os
        concorrentes e registre o que viu.
      </div>

      <div className="flex flex-wrap gap-2">
        <label className="min-w-64 flex-1">
          <span className="mb-1 block text-xs font-medium">Mercadoria</span>
          <select
            value={produtoId}
            onChange={(e) => {
              setProdutoId(e.target.value);
              setNovo(false);
              setEditando(null);
            }}
            className="w-full rounded border border-regua-2 bg-superficie px-3 py-1.5 text-sm outline-none focus:border-marca"
          >
            <option value="">Escolha…</option>
            {produtos
              .filter((p) => p.ativo)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
          </select>
        </label>
      </div>

      {!produtoId ? (
        <Vazio
          icone={<Search className="size-8" />}
          titulo="Escolha uma mercadoria"
          descricao="A pesquisa é por produto: cada um tem seus concorrentes."
        />
      ) : carregandoAnuncios ? (
        <Carregando />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-regua-2 bg-superficie p-4">
              <p className="text-xs text-tinta-3">Preço de referência</p>
              {referencia !== null ? (
                <p className="text-2xl font-semibold text-marca">{formatarReais(referencia)}</p>
              ) : (
                <p className="mt-1 text-sm text-tinta-3 italic">nenhum anúncio registrado</p>
              )}
              <select
                value={modo}
                onChange={(e) => setModo(e.target.value as ModoReferencia)}
                className="mt-2 w-full rounded border border-regua-2 bg-papel px-2 py-1 text-xs outline-none focus:border-marca"
              >
                {MODOS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[0.65rem] text-tinta-3">
                {MODOS.find((m) => m.id === modo)?.porque}
              </p>
            </div>

            <div className="rounded-md border border-regua-2 bg-superficie p-4">
              <p className="text-xs text-tinta-3">Anúncios registrados</p>
              <p className="text-2xl font-semibold">{precos.length}</p>
              {precos.length < minimo ? (
                <p className="mt-1 text-xs text-c-res">
                  O mínimo configurado é {minimo}. Com menos que isso a análise marca o critério como
                  não atendido — poucos anúncios não descrevem um mercado.
                </p>
              ) : (
                <p className="mt-1 text-xs text-c-via">Acima do mínimo de {minimo}.</p>
              )}
            </div>

            <div className="rounded-md border border-regua-2 bg-superficie p-4">
              <p className="text-xs text-tinta-3">Dispersão</p>
              {dispersao === null ? (
                <p className="mt-1 text-sm text-tinta-3 italic">precisa de dois anúncios</p>
              ) : (
                <>
                  <p
                    className={`text-2xl font-semibold ${
                      dispersao > dispersaoMaxima ? 'text-c-res' : ''
                    }`}
                  >
                    {formatarPct(dispersao)}
                  </p>
                  <p className="mt-1 text-xs text-tinta-3">
                    {dispersao > dispersaoMaxima
                      ? `Acima do limite de ${formatarPct(dispersaoMaxima)}: os preços estão espalhados demais para servirem de referência confiável.`
                      : 'Dentro do limite configurado.'}
                  </p>
                </>
              )}
            </div>
          </div>

          {podeEscrever && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setNovo(true);
                  setEditando(null);
                }}
                className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
              >
                <Plus className="size-4" aria-hidden />
                Registrar anúncio
              </button>
            </div>
          )}

          {novo && (
            <Formulario
              produtoId={produtoId}
              inicial={{ ...anuncioVazio(), qtdPorPacoteMilli: produto?.qtdPorEmbalagemMilli ?? 1_000 }}
              usuarioId={perfil?.id ?? ''}
              aoConcluir={() => setNovo(false)}
            />
          )}

          {anuncios.length === 0 && !novo ? (
            <Vazio
              icone={<Search className="size-8" />}
              titulo="Nenhum anúncio registrado"
              descricao="Abra o Mercado Livre, procure este produto e registre os concorrentes que encontrar. Quanto mais parecidos, melhor a referência."
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {anuncios.map((a) => (
                <li key={a.id} className="rounded-md border border-regua-2 bg-superficie">
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <span className="min-w-48 flex-1 font-medium">{a.titulo}</span>
                    <span className="text-xs text-tinta-2">
                      <Texto valor={a.vendedorNome} />
                    </span>
                    <span className="text-sm">
                      <Reais cents={a.precoCents} />
                    </span>
                    {a.qtdPorPacoteMilli !== 1_000 && (
                      <span className="text-xs text-c-res" title="Preço por unidade, já dividido pelo pacote.">
                        <Quantidade milli={a.qtdPorPacoteMilli} /> por pacote →{' '}
                        <Reais cents={a.precoUnitarioNormCents} /> cada
                      </span>
                    )}
                    {a.freteGratis && (
                      <span className="rounded bg-c-via/12 px-1.5 py-0.5 text-[0.65rem] text-c-via">
                        frete grátis
                      </span>
                    )}

                    <div className="ml-auto flex gap-1.5">
                      {a.link && (
                        <a
                          href={a.link}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="rounded border border-regua-2 px-2 py-1 text-tinta-3 hover:border-marca hover:text-marca"
                          aria-label="Abrir o anúncio"
                        >
                          <ExternalLink className="size-3.5" aria-hidden />
                        </a>
                      )}
                      {podeEscrever && (
                        <>
                          <button
                            type="button"
                            onClick={() => setEditando(editando === a.id ? null : a.id)}
                            className="rounded border border-regua-2 px-2.5 py-1 text-xs hover:border-marca hover:text-marca"
                          >
                            {editando === a.id ? 'Fechar' : 'Editar'}
                          </button>
                          <button
                            type="button"
                            onClick={() => removerAnuncio(produtoId, a.id)}
                            aria-label={`Remover ${a.titulo}`}
                            className="rounded border border-regua-2 px-2 py-1 text-tinta-3 hover:border-pend hover:text-pend"
                          >
                            <Trash2 className="size-3.5" aria-hidden />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {editando === a.id && (
                    <Formulario
                      produtoId={produtoId}
                      id={a.id}
                      inicial={a}
                      usuarioId={perfil?.id ?? ''}
                      aoConcluir={() => setEditando(null)}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function Formulario({
  produtoId,
  id,
  inicial,
  usuarioId,
  aoConcluir,
}: {
  produtoId: string;
  id?: string;
  inicial: RascunhoAnuncio;
  usuarioId: string;
  aoConcluir: () => void;
}) {
  const [d, setD] = useState<RascunhoAnuncio>(() => ({ ...inicial }));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const editar = <K extends keyof RascunhoAnuncio>(c: K, v: RascunhoAnuncio[K]) =>
    setD((x) => ({ ...x, [c]: v }));

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!d.titulo.trim()) return setErro('Copie o título do anúncio — é como você vai reconhecê-lo.');
    if (d.precoCents === null) return setErro('Informe o preço do anúncio.');
    setErro(null);
    setSalvando(true);
    try {
      await salvarAnuncio(produtoId, id ?? null, d, usuarioId);
      aoConcluir();
    } catch (ex) {
      setErro(ex instanceof Error ? ex.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={salvar} className="flex flex-col gap-3 border-t border-regua px-4 py-4">
      {erro && <Erro titulo={erro} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="sm:col-span-2">
          <CampoTexto
            rotulo="Título do anúncio"
            valor={d.titulo}
            aoMudar={(v) => editar('titulo', v ?? '')}
          />
        </div>
        <CampoTexto
          rotulo="Vendedor"
          valor={d.vendedorNome}
          aoMudar={(v) => editar('vendedorNome', v)}
        />
        <CampoReais
          rotulo="Preço anunciado"
          cents={d.precoCents}
          aoMudar={(v) => editar('precoCents', v)}
        />
        <CampoQuantidade
          rotulo="Unidades no pacote"
          milli={d.qtdPorPacoteMilli}
          aoMudar={(v) => editar('qtdPorPacoteMilli', v ?? 1_000)}
          dica="Kit com 3 peças? Ponha 3 — o preço por unidade sai dividido."
        />
        <CampoTexto rotulo="Condição" valor={d.condicao} aoMudar={(v) => editar('condicao', v)} />
        <CampoInteiro
          rotulo="Quantidade vendida"
          valor={d.quantidadeVendida}
          aoMudar={(v) => editar('quantidadeVendida', v)}
          dica="O que o anúncio mostra, quando mostra."
        />
        <CampoTexto
          rotulo="Reputação do vendedor"
          valor={d.reputacaoVendedor}
          aoMudar={(v) => editar('reputacaoVendedor', v)}
        />
        <div className="sm:col-span-2">
          <CampoTexto rotulo="Link" valor={d.link} aoMudar={(v) => editar('link', v)} />
        </div>
        <Campo rotulo="Frete grátis">
          <label className="flex items-center gap-2 py-1.5 text-sm">
            <input
              type="checkbox"
              checked={d.freteGratis === true}
              onChange={(e) => editar('freteGratis', e.target.checked)}
            />
            <span className="text-tinta-2">O anúncio oferece</span>
          </label>
        </Campo>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={salvando}
          className="rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar anúncio'}
        </button>
        <button
          type="button"
          onClick={aoConcluir}
          className="rounded border border-regua-2 px-4 py-1.5 text-sm hover:border-marca"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
